// RelatorioLote.js - Utilitários para relatórios de lotes
import { notas } from '../../services/api'
import JsBarcode from 'jsbarcode'

/**
 * Abre um relatório de lote em uma nova janela ou aba
 * @param {Object} params - Parâmetros do relatório
 * @param {string} params.loteId - ID do lote
 * @param {Object} params.meta - Metadados do lote
 * @param {Array} params.itens - Itens do lote
 * @param {Object} params.user - Usuário atual
 */
export const abrirRelatorioLote = async ({ loteId, meta, itens, user }) => {
  try {
    // Validar parâmetros obrigatórios
    if (!loteId) {
      throw new Error('ID do lote é obrigatório')
    }

    if (!itens || !Array.isArray(itens) || itens.length === 0) {
      throw new Error('Itens do lote são obrigatórios')
    }

    // Buscar histórico para cada item
    const itensComHistorico = await Promise.all(itens.map(async (item) => {
      const chave = item.chave_nfe || item.chave || ''
      let alteracoes = []
      if (chave) {
        try {
          alteracoes = await notas.getHistorico(chave)
        } catch (e) {
          console.warn(`Erro ao buscar histórico para nota ${chave}:`, e)
        }
      }
      return {
        ...item,
        alteracoes
      }
    }))

    // Preparar dados para o relatório
    const dadosRelatorio = {
      loteId,
      meta: {
        lote_id: meta?.lote_id || loteId,
        data_criacao: meta?.data_criacao || new Date().toISOString(),
        descricao: meta?.descricao || `Lote ${loteId}`,
        carro: meta?.carro || '',
        empresa: meta?.empresa || '',
        usuario: meta?.usuario || user?.nome || user?.username || 'Usuário',
        data_geracao: new Date().toLocaleString('pt-BR'),
        total_itens: itens.length
      },
      itens: itensComHistorico.map((item, index) => ({
        ...item,
        sequencia: index + 1
      }))
    }

    // Gerar HTML do relatório
    const htmlRelatorio = gerarHtmlRelatorio(dadosRelatorio)

    // Abrir em nova janela
    const novaJanela = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')

    if (novaJanela) {
      novaJanela.document.write(htmlRelatorio)
      novaJanela.document.close()
      novaJanela.focus()
    } else {
      // Fallback: criar blob e download
      const blob = new Blob([htmlRelatorio], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `relatorio_lote_${loteId}_${new Date().getTime()}.html`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }

  } catch (error) {
    console.error('Erro ao abrir relatório de lote:', error)
    throw error
  }
}

// Gera SVG de código de barras (CODE128) a partir da chave
function criaSvgBarcode(texto) {
  const normalized = String(texto || '').replace(/[^a-zA-Z0-9]/g, '')
  const svgBarcode = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svgBarcode.classList.add('barcode')
  svgBarcode.setAttribute('data-value', normalized)
  JsBarcode(svgBarcode, normalized, {
    format: 'CODE128',
    width: 2,
    height: 100,
    margin: 0,
    displayValue: false,
  })
  let svgString = svgBarcode.outerHTML
  svgString = svgString.replace(/<g transform="translate\(\d+, \d+\)"/, '<g')
  return svgString
}

// Função para gerar linha adicional para registros manuais
function gerarLinhaManual(it) {
  const destinatarioStr = it.cddestinatario || it.CDDESTINATARIO || '-'
  const cepStr = it.cepentrega || it.CEPENTREGA ? String(it.cepentrega || it.CEPENTREGA).replace(/(\d{5})(\d{3})/, '$1-$2') : '-'
  const pesoStr = it.peso || it.PESO ? String(it.peso || it.PESO) : '-'
  const m3Str = it.m3 || it.M3 ? String(it.m3 || it.M3) : '-'
  const pedidoStr = it.pedido || it.PEDIDO || '0'
  const enderecoStr = it.endereco || it.ENDERECO || ''

  return `
    <tr style="font-family: Consolas, monospace; height: 30px; background-color: #f0f8ff; border-left: 3px solid #52c41a;">
      <td style="width: 3ch; text-align: center; padding: 4px 8px; font-style: italic;">→</td>
      <td style="width: 9ch; text-align: left; padding: 4px 8px; font-size: 11px; color: #666;">MANUAL</td>
      <td colspan="6" style="text-align: left; padding: 4px 8px; font-size: 11px;">
        <strong>Destinatário:</strong> ${destinatarioStr} | 
        <strong>Cep:</strong> ${cepStr} | 
        <strong>Peso:</strong> ${pesoStr} | 
        <strong>M3:</strong> ${m3Str} | 
        <strong>Pedido:</strong> ${pedidoStr} | 
        <strong>Endereço:</strong> ${enderecoStr}
      </td>
    </tr>
  `
}

function agruparPorRemetente(items) {
  const grupos = {}
  items.forEach(item => {
    // Tentar identificar o remetente por vários campos possíveis
    const cnpj = item.cnpj || item.CNPJ || item.cnpj_remetente || item.CNPJ_REMETENTE || item.originalData?.cnpj_remetente || item.originalData?.CNPJ || ''
    const remetente = item.remetente || item.REMETENTE || item.remetente_nome || item.REMETENTE_NOME || item.originalData?.remetente || item.originalData?.REMETENTE || 'Remetente não informado'

    const chave = cnpj || remetente || 'SEM_REMETENTE'
    if (!grupos[chave]) {
      grupos[chave] = {
        cnpj: cnpj,
        remetente: remetente,
        itens: []
      }
    }
    grupos[chave].itens.push(item)
  })
  return grupos
}

function gerarLinhasAlteracao(it) {
  const val = it?.alteracoes ?? it?.ALTERACOES ?? ''
  if (!val) return ''

  try {
    let alteracoes = val
    if (typeof alteracoes === 'string') {
      const str = alteracoes.trim()
      if (str.startsWith('[') || str.startsWith('{')) {
        alteracoes = JSON.parse(str)
      } else {
        alteracoes = [{ texto: str }]
      }
    }

    const renderRow = (alt) => {
      if (typeof alt === 'string') {
        return `
          <tr style="font-family: Consolas, monospace; height: 30px; background-color: #fff7e6; border-left: 3px solid #fa8c16;">
            <td style="width: 3ch; text-align: center; padding: 4px 8px; font-style: italic;">⚠</td>
            <td style="width: 9ch; text-align: left; padding: 4px 8px; font-size: 11px; color: #666;">ALT</td>
            <td colspan="6" style="text-align: left; padding: 4px 8px; font-size: 11px;">${alt}</td>
          </tr>
        `
      }

      // Mapeamento para os campos da tabela nota_historico
      const acaoStr = alt.operacao || alt.acao || alt.action || 'N/A'
      const campoStr = alt.campo || ''
      const antesStr = alt.valorAnterior || alt.antes || alt.from || 'N/A'
      const depoisStr = alt.valorNovo || alt.depois || alt.to || 'N/A'
      const usuarioStr = alt.usuario || alt.user || 'N/A'
      const dataStr = alt.dataOperacao ? new Date(alt.dataOperacao).toLocaleString('pt-BR') : (alt.data ? new Date(alt.data).toLocaleString('pt-BR') : 'N/A')

      return `
        <tr style="font-family: Consolas, monospace; height: 30px; background-color: #fff7e6; border-left: 3px solid #fa8c16;">
          <td style="width: 3ch; text-align: center; padding: 4px 8px; font-style: italic;">⚠</td>
          <td style="width: 9ch; text-align: left; padding: 4px 8px; font-size: 11px; color: #666;">ALT</td>
          <td colspan="6" style="text-align: left; padding: 4px 8px; font-size: 11px;">
            <strong>Ação:</strong> ${acaoStr} | 
            <strong>Campo:</strong> ${campoStr} |
            <strong>Antes:</strong> ${antesStr} | 
            <strong>Depois:</strong> ${depoisStr} | 
            <strong>Usuário:</strong> ${usuarioStr} | 
            <strong>Data:</strong> ${dataStr}
          </td>
        </tr>
      `
    }

    if (Array.isArray(alteracoes) && alteracoes.length > 0) {
      return alteracoes.map(renderRow).join('')
    }

    if (alteracoes && typeof alteracoes === 'object' && !Array.isArray(alteracoes)) {
      return renderRow(alteracoes)
    }

    return ''
  } catch (error) {
    console.warn('Erro ao processar alterações no relatório:', error)
    const plain = typeof val === 'string' ? val : JSON.stringify(val)
    return `
      <tr style="font-family: Consolas, monospace; height: 30px; background-color: #fff7e6; border-left: 3px solid #fa8c16;">
        <td style="width: 3ch; text-align: center; padding: 4px 8px; font-style: italic;">⚠</td>
        <td style="width: 9ch; text-align: left; padding: 4px 8px; font-size: 11px; color: #666;">ALT</td>
        <td colspan="6" style="text-align: left; padding: 4px 8px; font-size: 11px;">${plain}</td>
      </tr>
    `
  }
}

// Função para gerar cabeçalho do remetente
function gerarCabecalhoRemetente(remetente, cnpj, totalItens) {
  return `
    <div style="margin: 10px 0 10px 0; padding: 15px; background-color: #f5f5f5; border-left: 4px solid #1890ff; border-radius: 4px; display: flex; align-items: center; gap: 12px;">
      <h3 style="margin: 0; color: #1890ff; font-size: 14px; font-weight: 600;">
        ${remetente}
      </h3>
      <p style="margin: 0; color: #666; font-size: 12px;">
        <strong>CNPJ:</strong> ${cnpj || 'Não informado'} | 
        <strong>Total de itens:</strong> ${totalItens}
      </p>
    </div>
  `
}

// Função para gerar cabeçalho da tabela
function gerarCabecalhoTabela() {
  return `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; table-layout: fixed; border: 1px solid #000;">
      <thead>
        <tr style="background-color: #f0f0f0; font-weight: bold;">
          <th style="width: 3ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Seq</th>
          <th style="width: 9ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Nota</th>
          <th style="width: 20ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Destinatário</th>
          <th style="width: 20ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Cidade</th>
          <th style="width: 2ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">UF</th>
          <th style="width: 4ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Vol</th>
          <th style="width: 8ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Frete</th>
          <th style="width: 40ch; text-align: center; padding: 4px 8px; border: 1px solid #000; font-family: Consolas, monospace;">Chave</th>
        </tr>
      </thead>
      <tbody>
  `
}

// Função para gerar linhas de um grupo de itens
function gerarLinhasGrupo(itens) {
  return itens.map((it, idx) => {
    const nota = it.NOTA || it.nota || ''
    const serie = it.SERIE || it.serie || ''
    const notaStr = serie ? `${String(nota).padStart(9, ' ')}/${String(serie)}` : String(nota).padStart(9, ' ')
    const destinatarioStr = String(it.destinatario || it.REMETENTE || '').substring(0, 20)
    const cidadeStr = String(it.cidade || '').substring(0, 20)
    const ufStr = it.uf || ''
    const volumes = it.VOLUMES || it.volumes || 0
    const volStr = String(volumes).padStart(4, ' ')
    const valorTotal = it.VALORTOTAL || it.valorTotal || 0
    const freteStr = Math.round(Number(valorTotal)).toString()
    const chaveStr = it.chave_nfe || it.chave || ''
    const normalizedChave = String(chaveStr || '').replace(/[^a-zA-Z0-9]/g, '')
    const isValidChave = normalizedChave.length === 44 && /^\d+$/.test(normalizedChave)

    let chaveCellContent = chaveStr
    if (isValidChave) {
      try {
        chaveCellContent = criaSvgBarcode(normalizedChave)
      } catch (e) {
        chaveCellContent = chaveStr
      }
    }

    // Linha principal do item
    let linhaPrincipal = `
      <tr style="font-family: Consolas, monospace; height: 70px; border-bottom: 1px solid #eee;">
        <td style="width: 3ch; text-align: right; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${String(it.sequencia || idx + 1).padStart(3, ' ')}</td>
        <td style="width: 9ch; text-align: right; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${notaStr}</td>
        <td style="width: 20ch; text-align: left; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${destinatarioStr}</td>
        <td style="width: 20ch; text-align: left; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${cidadeStr}</td>
        <td style="width: 2ch; text-align: center; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${ufStr}</td>
        <td style="width: 4ch; text-align: right; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${volStr}</td>
        <td style="width: 8ch; text-align: right; padding: 4px 8px; border: 1px solid #000; vertical-align: middle;">${freteStr}</td>
        <td style="width: 40ch; text-align: center; padding: 5px 15px; border: 1px solid #000; vertical-align: middle;">${chaveCellContent}</td>
      </tr>
    `

    // Se for inclusão manual, adicionar linha adicional com dados específicos
    if (it.inclusao === 'MANUAL' || it.STATUS === 'MANUAL' || it.status === 'MANUAL') {
      linhaPrincipal += gerarLinhaManual(it)
    }

    // Se houver alterações, adicionar linhas adicionais para cada alteração
    if (it.alteracoes && it.alteracoes.length > 0) {
      linhaPrincipal += gerarLinhasAlteracao(it)
    }

    return linhaPrincipal
  }).join('')
}

/**
 * Gera o HTML do relatório de lote
 * @param {Object} dados - Dados do relatório
 * @returns {string} - HTML do relatório
 */
const gerarHtmlRelatorio = (dados) => {
  const { meta, itens } = dados
  const gruposRemetente = agruparPorRemetente(itens)

  const tabelasRemetente = Object.keys(gruposRemetente).map(chave => {
    const grupo = gruposRemetente[chave]
    const cabecalhoRemetente = gerarCabecalhoRemetente(grupo.remetente, grupo.cnpj, grupo.itens.length)
    const cabecalhoTabela = gerarCabecalhoTabela()
    const linhasGrupo = gerarLinhasGrupo(grupo.itens)

    return `
      ${cabecalhoRemetente}
      ${cabecalhoTabela}
      ${linhasGrupo}
      </tbody>
      </table>
    `
  }).join('')

  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Relatório de Lote - ${meta.lote_id}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 15px;
      background: #ffffff;
      color: #000000;
      width: 1000px;
      margin: 0 auto;
    }
    .no-print { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .btn { padding: 8px 16px; margin-right: 8px; border: 1px solid #d9d9d9; background: #fff; cursor: pointer; border-radius: 4px; }
    .btn-primary { background: #1890ff; color: white; border-color: #1890ff; }
    .btn:hover { opacity: 0.8; }
    .header { padding: 12px; margin-bottom: 0; display: flex; align-items: center; }
    .tabelas-remetente { margin-top: 0; }
    .logo { height: 40px; width: 60px; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; line-height: 1; background-color: #ff6900; color: #000; margin-right: 12px; }
    .logo div { font-size: 14px; }
    .info { color: #000; }
    .info-title { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
    .info-date { font-size: 14px; }
    .info-operator { font-size: 12px; margin-top: 2px; }
    .info-desc { font-size: 12px; color: #666; }
    table { width: 800px; border-collapse: collapse; margin-top: 8px; table-layout: fixed; border: 1px solid #000; }
    th, td { border: 1px solid #000; }
    th { font-family: Consolas, monospace; padding: 4px 8px; background: #f5f5f5; }
    .barcode { padding: 0; margin: 0; width: 100%; height: 100%; }
    @media print {
      @page { margin: 0; }
      body { width: 9in; height: 11.69in; margin: 15px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
  </style>

</head>
<body>
    <!-- Controles -->
    <div class="no-print">
        <div>
            <button class="btn" onclick="window.close()">← Voltar</button>
            <button class="btn btn-primary" onclick="window.print()">🖨️ Imprimir</button>
        </div>
    </div>

    <!-- Cabeçalho -->
    <div class="header">
        <div class="logo">
            <div>${meta.empresa || 'Empresa'}</div>
        </div>
        <div class="info">
            <div class="info-title">Relatório de Lote</div>
            <div class="info-date">${new Date(meta.data_criacao).toLocaleDateString('pt-BR')}${meta.carro ? ` • Carro: ${meta.carro}` : ''}</div>
            <div class="info-operator">Operador: ${meta.usuario || 'N/A'} • Empresa: ${meta.empresa || 'Empresa'}</div>
            <div class="info-desc">Descrição:<span> ${meta.descricao}</span> • Lote: ${meta.lote_id}</div>
        </div>
    </div>

    <div class="tabelas-remetente">
        ${tabelasRemetente}
    </div>

    <div style="margin-top: 20px; text-align: center; color: #666; font-size: 10px; border-top: 1px solid #eee; padding-top: 10px;">
        <p>Relatório gerado em ${meta.data_geracao} por ${meta.usuario}</p>
        <p>Sistema de Etiquetagem - Lote ${meta.lote_id}</p>
    </div>
</body>
</html>
  `
}

/**
 * Exporta dados do lote para CSV
 * @param {Object} dados - Dados do lote
 */
export const exportarLoteCSV = (dados) => {
  try {
    const { meta, itens } = dados

    // Cabeçalho do CSV
    const headers = [
      'Sequencia',
      'Nota',
      'Serie',
      'CNPJ',
      'Destinatario',
      'Cidade',
      'UF',
      'CEP',
      'Volumes',
      'Peso',
      'M3',
      'Status'
    ]

    // Converter itens para CSV
    const csvContent = [
      headers.join(','),
      ...itens.map(item => [
        item.sequencia || '',
        item.NOTA || item.nota || '',
        item.SERIE || item.serie || '',
        item.CNPJ || item.cnpj || item.cddestinatario || '',
        (item.destinatario || item.REMETENTE || '').replace(/,/g, ';'),
        (item.cidade || '').replace(/,/g, ';'),
        item.uf || '',
        item.CEPENTREGA || item.cepentrega || '',
        item.VOLUMES || item.volumes || 0,
        Number(item.PESO || item.peso || 0).toFixed(2),
        Number(item.M3 || item.m3 || 0).toFixed(3),
        item.STATUS || item.status || ''
      ].join(','))
    ].join('\n')

    // Download do arquivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `lote_${meta.lote_id}_${new Date().getTime()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

  } catch (error) {
    console.error('Erro ao exportar CSV:', error)
    throw error
  }
}